import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Download, Edit2, Trash2, ChevronDown } from 'lucide-react';
import './FindingsNotes.css';

const API_URL = process.env.REACT_APP_API_URL || '';

const categories = [
  'Clients & Revenue',
  'Front Office (Sales & Delivery)',
  'Operations & Back Office',
  'Legal & Corporate',
  'Finance, Accounting, Tax, QoE, NWC',
  'Valuation/Terms Items',
  'Integration Items',
  'Miscellaneous'
];

function FindingsNotes({ dealId: initialDealId }) {
  const [findings, setFindings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFinding, setEditingFinding] = useState(null);
  const [deals, setDeals] = useState([]);
  const [selectedDealId, setSelectedDealId] = useState(initialDealId);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: categories[0],
    question: '',
    finding: '',
    sourceDocument: ''
  });

  // Fetch available deals
  useEffect(() => {
    fetchDeals();
  }, []);

  // Fetch findings when selected deal changes
  useEffect(() => {
    if (selectedDealId) {
      fetchFindings();
    } else {
      setLoading(false);
      setFindings([]);
    }
  }, [selectedDealId]);

  const fetchDeals = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/deals`, {
        withCredentials: true
      });
      const dealsData = response.data || [];
      setDeals(dealsData);
      // If no deal selected and deals exist, select first one
      if (!selectedDealId && dealsData.length > 0) {
        setSelectedDealId(dealsData[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch deals:', error);
    }
  };

  const fetchFindings = async () => {
    if (!selectedDealId) return;
    try {
      setLoading(true);
      console.log('Fetching findings for deal:', selectedDealId);
      const response = await axios.get(`${API_URL}/api/deals/${selectedDealId}/notes`, {
        withCredentials: true
      });
      console.log('Fetch response:', response.data);
      setFindings(response.data || []);
    } catch (error) {
      console.error('Failed to fetch findings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    console.log('handleAdd called', { selectedDealId, formData });
    if (!selectedDealId) {
      alert('Please select a deal first');
      return;
    }
    try {
      if (editingFinding) {
        console.log('Updating finding:', editingFinding.id);
        await axios.put(`${API_URL}/api/deals/${selectedDealId}/notes/${editingFinding.id}`, formData, {
          withCredentials: true
        });
      } else {
        console.log('Creating new finding');
        const response = await axios.post(`${API_URL}/api/deals/${selectedDealId}/notes`, formData, {
          withCredentials: true
        });
        console.log('Create response:', response.data);
      }
      console.log('Success, closing modal and refreshing');
      setShowModal(false);
      setEditingFinding(null);
      setFormData({
        date: new Date().toISOString().split('T')[0],
        category: categories[0],
        question: '',
        finding: '',
        sourceDocument: ''
      });
      fetchFindings();
    } catch (error) {
      console.error('Failed to save finding:', error);
      alert('Failed to save finding. Please try again.');
    }
  };

  const handleEdit = (finding) => {
    setEditingFinding(finding);
    setFormData({
      date: finding.date,
      category: finding.category,
      question: finding.question || '',
      finding: finding.finding,
      sourceDocument: finding.sourceDocument || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (findingId) => {
    if (!selectedDealId) return;
    if (!window.confirm('Are you sure you want to delete this finding?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/deals/${selectedDealId}/notes/${findingId}`, {
        withCredentials: true
      });
      fetchFindings();
    } catch (error) {
      console.error('Failed to delete finding:', error);
      alert('Failed to delete finding. Please try again.');
    }
  };

  const handleDownload = async () => {
    if (!selectedDealId) {
      alert('Please select a deal first');
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/deals/${selectedDealId}/notes/report`, {
        withCredentials: true
      });
      
      // Create a new window with the HTML report
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data);
      printWindow.document.close();
      
      // Wait for content to load then print
      printWindow.onload = () => {
        printWindow.print();
      };
    } catch (error) {
      console.error('Failed to generate report:', error);
      alert('Failed to generate report. Please try again.');
    }
  };

  // Group findings by category (ensure findings is an array)
  const findingsArray = Array.isArray(findings) ? findings : [];
  const groupedFindings = findingsArray.reduce((acc, finding) => {
    if (!acc[finding.category]) acc[finding.category] = [];
    acc[finding.category].push(finding);
    return acc;
  }, {});

  // Sort categories by order they appear in the categories array
  const sortedCategories = Object.keys(groupedFindings).sort(
    (a, b) => categories.indexOf(a) - categories.indexOf(b)
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
  };

  if (loading) {
    return <div className="findings-container">Loading findings...</div>;
  }

  const selectedDeal = deals.find(d => d.id === selectedDealId);

  if (deals.length === 0) {
    return (
      <div className="findings-container">
        <div className="findings-empty">
          <div className="findings-empty-icon">📋</div>
          <h3>No Deals Available</h3>
          <p>Create a deal first to start adding findings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="findings-container">
      <div className="findings-header">
        <div className="findings-header-left">
          <h2 className="findings-title">Due Diligence Findings Notes</h2>
          <div className="findings-deal-selector">
            <label>Deal:</label>
            <select 
              value={selectedDealId || ''} 
              onChange={(e) => {
                console.log('Deal changed to:', e.target.value);
                setSelectedDealId(e.target.value);
              }}
              className="findings-deal-select"
            >
              {deals.map(deal => (
                <option key={deal.id} value={deal.id}>{deal.name}</option>
              ))}
            </select>
            <ChevronDown size={16} className="findings-deal-chevron" />
          </div>
        </div>
        <div className="findings-actions">
          <button className="findings-btn findings-btn-secondary" onClick={handleDownload}>
            <Download size={16} />
            Download Report
          </button>
          <button 
            className="findings-btn findings-btn-primary" 
            onClick={() => {
              console.log('Add Finding clicked');
              setEditingFinding(null);
              setFormData({
                date: new Date().toISOString().split('T')[0],
                category: categories[0],
                question: '',
                finding: '',
                sourceDocument: ''
              });
              setShowModal(true);
              console.log('showModal set to true');
            }}
          >
            <Plus size={16} />
            Add Finding
          </button>
        </div>
      </div>

      {findingsArray.length === 0 ? (
        <div className="findings-empty">
          <div className="findings-empty-icon">📝</div>
          <h3>No findings recorded yet</h3>
          <p>Start adding findings to build your due diligence report.</p>
        </div>
      ) : (
        sortedCategories.map(category => (
          <div key={category} className="findings-category">
            <div className="findings-category-header">
              <span>{category}</span>
              <span className="findings-category-count">
                {groupedFindings[category].length}
              </span>
            </div>
            <table className="findings-table">
              <thead>
                <tr>
                  <th className="date-col">Date</th>
                  <th>Key Questions & Findings</th>
                  <th className="actions-col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {groupedFindings[category].map(finding => (
                  <tr key={finding.id} className={finding.isAnnotation ? 'findings-annotation-row' : ''}>
                    <td className="findings-date">{formatDate(finding.date)}</td>
                    <td>
                      {finding.question && (
                        <div className="findings-question">{finding.question}</div>
                      )}
                      <div className="findings-text">{finding.finding}</div>
                      {finding.sourceDocument && (
                        <div className="findings-source">
                          📄 {finding.sourceDocument}
                        </div>
                      )}
                    </td>
                    <td>
                      {!finding.isAnnotation && (
                        <div className="findings-actions-cell">
                          <button 
                            className="findings-action-btn"
                            onClick={() => handleEdit(finding)}
                            title="Edit"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            className="findings-action-btn delete"
                            onClick={() => handleDelete(finding.id)}
                            title="Delete"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))
      )}

      {showModal && (
        <div 
          className="findings-modal-overlay" 
          onClick={() => setShowModal(false)} 
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999
          }}
        >
          {console.log('Modal is rendering')}
          <div className="findings-modal" onClick={e => e.stopPropagation()}>
            <div className="findings-modal-header">
              <h3 className="findings-modal-title">
                {editingFinding ? 'Edit Finding' : 'Add New Finding'}
              </h3>
              <button className="findings-modal-close" onClick={() => setShowModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleAdd}>
              <div className="findings-modal-body">
                <div className="findings-form-group">
                  <label>Date</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={e => setFormData({...formData, date: e.target.value})}
                    required
                  />
                </div>
                <div className="findings-form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData({...formData, category: e.target.value})}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
                <div className="findings-form-group">
                  <label>Key Question (optional)</label>
                  <input
                    type="text"
                    value={formData.question}
                    onChange={e => setFormData({...formData, question: e.target.value})}
                    placeholder="e.g., What are the key client risks?"
                  />
                </div>
                <div className="findings-form-group">
                  <label>Finding *</label>
                  <textarea
                    value={formData.finding}
                    onChange={e => setFormData({...formData, finding: e.target.value})}
                    placeholder="Describe the finding, risk, or opportunity..."
                    required
                  />
                </div>
                <div className="findings-form-group">
                  <label>Source Document (optional)</label>
                  <input
                    type="text"
                    value={formData.sourceDocument}
                    onChange={e => setFormData({...formData, sourceDocument: e.target.value})}
                    placeholder="e.g., Company_A_Financial_Package.pdf"
                  />
                </div>
              </div>
              <div className="findings-modal-footer">
                <button 
                  type="button" 
                  className="findings-btn-cancel"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="findings-btn-save">
                  {editingFinding ? 'Update Finding' : 'Add Finding'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default FindingsNotes;
